import React, { useCallback, useContext } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from 'api';
import AgreementSetup from 'components/AgreementSetup';
import { getAgreementTemplateQueryKey } from 'hooks/useAgreementTemplateQuery';
import { ActivateAgreementBody } from 'models/Agreement';
import ListingContext from '../../providers/Listing/Listing.context';
import { getAgreementPageBackPath } from '../../utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

const AgreementSetupPage: React.FC = () => {
  const {
    areActionsDisabled,
    listing: { _id: listingId, isNativeEsignEnabled },
    dealRoom: { name: dealName },
    setListing,
  } = useContext(ListingContext);

  const { popSuccess } = useContext(PopNotificationsContext);

  const navigate = useNavigate();
  const { state } = useLocation();
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const backPath = getAgreementPageBackPath(state);

  const activateAgreement = useCallback(
    async (body: ActivateAgreementBody) => {
      const { data } = await api.agreements.activateAgreement(listingId, body);

      queryClient.setQueryData(getAgreementTemplateQueryKey(listingId), data);
      setListing((prev) => ({ ...prev, agreementTemplateId: data._id }));
    },
    [listingId, queryClient, setListing],
  );

  const finishActivation = useCallback(() => {
    popSuccess(t('agreementActivated'));
    navigate(backPath);
  }, [backPath, navigate, popSuccess, t]);

  const disableNativeEsign = useCallback(
    () => setListing((prev) => ({ ...prev, isNativeEsignEnabled: false })),
    [setListing],
  );

  if (!isNativeEsignEnabled) return <Navigate to={backPath} replace />;

  return (
    <AgreementSetup
      backPath={backPath}
      backLabel={dealName}
      isDisabled={areActionsDisabled}
      onActivate={activateAgreement}
      onActivated={finishActivation}
      onDocusignConnected={disableNativeEsign}
    />
  );
};

export default AgreementSetupPage;
